import { ModelResolutionError } from "../../errors.js";
import type { AiModelRecord } from "../../models/types.js";
import {
  loadOpenRouterRoutingEnv,
  shouldDefaultRouteViaOpenRouter,
} from "../openRouterRoutingEnv.js";
import { buildCatalogIndexes } from "./catalogIndexes.js";
import {
  DEFAULT_CONFIDENCE_THRESHOLD,
  LOCAL_PROVIDERS,
  PROVIDER_INFERENCE_MAP,
} from "./constants.js";
import { modelSlug, normalizeProvider, normalizeString, tokenise } from "./normalize.js";
import { stripModelSlugSuffix, stripModelVersionSuffix } from "./stripVersionSuffix.js";
import type {
  CatalogIndexes,
  ModelResolutionInput,
  ModelResolutionNotFound,
  ModelResolutionResult,
  ModelResolutionSuccess,
  ModelResolverOptions,
  ResolutionStrategy,
  ResolverContext,
} from "./types.js";

type MatchCandidate = {
  modelId: string;
  record: AiModelRecord | null;
  confidence: number;
  strategy: ResolutionStrategy;
  reason: string;
};

export class ModelNameResolver {
  private readonly catalog: Map<string, AiModelRecord>;
  private readonly indexes: CatalogIndexes;
  private readonly ctx: ResolverContext;

  constructor(
    catalog: Map<string, AiModelRecord>,
    options: ModelResolverOptions = {},
  ) {
    this.catalog = catalog;
    this.indexes = buildCatalogIndexes(catalog);
    this.ctx = {
      catalog,
      indexes: this.indexes,
      options: {
        confidenceThreshold: options.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD,
        additionalProviderPatterns: options.additionalProviderPatterns ?? [],
        additionalLocalProviders: options.additionalLocalProviders ?? [],
        routingEnv: options.routingEnv ?? loadOpenRouterRoutingEnv(),
        routeViaOpenRouter: options.routeViaOpenRouter,
      },
    };
  }

  resolve(input: ModelResolutionInput): ModelResolutionResult {
    const attempted: ResolutionStrategy[] = [];
    const threshold = this.ctx.options.confidenceThreshold;

    const provider = normalizeProvider(input.provider);
    const model = normalizeString(input.model);
    const normalisedInput = model;

    // S10 — local provider (before catalog strategies)
    const localProviders = new Set([
      ...LOCAL_PROVIDERS,
      ...this.ctx.options.additionalLocalProviders.map((p) => normalizeString(p)),
    ]);
    if (provider && localProviders.has(provider)) {
      attempted.push("local-provider-passthrough");
      return this.success({
        modelId: model,
        record: null,
        confidence: 0.99,
        strategy: "local-provider-passthrough",
        reason: `Local provider "${provider}" — model not looked up in catalog`,
        resolvedVia: attempted,
        normalisedInput,
        provider,
        originalProvider: input.provider,
      });
    }

    const { match, rejected } = this.runPipeline(model, provider, attempted, threshold, []);
    if (match) {
      return this.success({
        modelId: match.modelId,
        record: match.record,
        confidence: match.confidence,
        strategy: match.strategy,
        reason: match.reason,
        resolvedVia: match.resolvedVia,
        normalisedInput,
        provider,
        originalProvider: input.provider,
      });
    }

    const notFound: ModelResolutionNotFound = {
      found: false,
      modelId: null,
      record: null,
      attemptedStrategies: attempted,
      reason: "No catalog entry matched after all strategies.",
      bestRejectedCandidate: rejected,
    };
    return notFound;
  }

  resolveMany(inputs: ModelResolutionInput[]): ModelResolutionResult[] {
    return inputs.map((i) => this.resolve(i));
  }

  resolveOrThrow(input: ModelResolutionInput): ModelResolutionSuccess {
    const result = this.resolve(input);
    if (result.found) return result;
    throw new ModelResolutionError(input, result);
  }

  private runPipeline(
    model: string,
    provider: string | undefined,
    attempted: ResolutionStrategy[],
    threshold: number,
    resolvedVia: ResolutionStrategy[],
    depth = 0,
  ): {
    match: (MatchCandidate & { resolvedVia: ResolutionStrategy[] }) | null;
    rejected?: ModelResolutionNotFound["bestRejectedCandidate"];
  } {
    if (depth > 12) return { match: null };

    const tryMatch = (
      strategy: ResolutionStrategy,
      fn: () => MatchCandidate | null,
    ): (MatchCandidate & { resolvedVia: ResolutionStrategy[] }) | null => {
      if (!attempted.includes(strategy)) attempted.push(strategy);
      const m = fn();
      if (m && m.confidence >= threshold) {
        return { ...m, resolvedVia: [...resolvedVia, strategy] };
      }
      return null;
    };

    let hit = tryMatch("exact-match", () => this.exactMatch(model));
    if (hit) return { match: hit };

    hit = tryMatch("catalog-alias-match", () => this.catalogAliasMatch(model));
    if (hit) return { match: hit };

    hit = tryMatch("canonical-slug-match", () => this.slugMatch(model));
    if (hit) return { match: hit };

    hit = tryMatch("provider-prefix-injection", () =>
      this.providerPrefixInjection(model, provider),
    );
    if (hit) return { match: hit };

    hit = tryMatch("cross-provider-correction", () => this.crossProviderCorrection(model));
    if (hit) return { match: hit };

    const stripped = stripModelVersionSuffix(model);
    if (stripped && stripped !== model) {
      if (!attempted.includes("version-suffix-strip")) attempted.push("version-suffix-strip");
      const inner = this.runPipeline(
        stripped,
        provider,
        attempted,
        threshold,
        [...resolvedVia, "version-suffix-strip"],
        depth + 1,
      );
      if (inner.match) {
        return {
          match: {
            ...inner.match,
            confidence: 0.85,
            strategy: "version-suffix-strip",
            reason: `Stripped version suffix from "${model}" → "${stripped}" then ${inner.match.reason}`,
            resolvedVia: [...resolvedVia, "version-suffix-strip", ...inner.match.resolvedVia],
          },
        };
      }
    }

    hit = tryMatch("date-suffix-strip", () => this.dateSuffixMatch(model));
    if (hit) return { match: hit };

    const partial = this.partialMatch(model, provider);
    if (partial) {
      if (!attempted.includes("partial-name-match")) attempted.push("partial-name-match");
      if (partial.confidence >= threshold) {
        return {
          match: { ...partial, resolvedVia: [...resolvedVia, "partial-name-match"] },
        };
      }
      return {
        match: null,
        rejected: {
          modelId: partial.modelId,
          confidence: partial.confidence,
          reason: partial.reason,
        },
      };
    }

    return { match: null };
  }

  private exactMatch(model: string): MatchCandidate | null {
    const record = this.catalog.get(model);
    if (!record) return null;
    return {
      modelId: model,
      record,
      confidence: 1,
      strategy: "exact-match",
      reason: `Exact catalog match for "${model}"`,
    };
  }

  private catalogAliasMatch(model: string): MatchCandidate | null {
    const modelId = this.indexes.aliasIndex.get(model);
    if (!modelId) return null;
    const record = this.catalog.get(modelId);
    if (!record) return null;
    return {
      modelId,
      record,
      confidence: 1,
      strategy: "catalog-alias-match",
      reason: `Catalog alias "${model}" → "${modelId}"`,
    };
  }

  private slugMatch(model: string): MatchCandidate | null {
    const modelId = this.indexes.slugIndex.get(model);
    if (!modelId) return null;
    const record = this.catalog.get(modelId);
    if (!record) return null;
    return {
      modelId,
      record,
      confidence: 0.95,
      strategy: "canonical-slug-match",
      reason: `Canonical slug match "${model}" → "${modelId}"`,
    };
  }

  private inferProviderFromSlug(slug: string): string | undefined {
    const patterns = [
      ...this.ctx.options.additionalProviderPatterns,
      ...PROVIDER_INFERENCE_MAP,
    ];
    for (const { pattern, provider } of patterns) {
      if (pattern.test(slug)) return provider;
    }
    return undefined;
  }

  private providerPrefixInjection(
    model: string,
    provider: string | undefined,
  ): MatchCandidate | null {
    if (model.includes("/")) return null;

    const prefixes: string[] = [];
    if (provider && provider !== "openrouter") {
      prefixes.push(provider);
    }
    const inferred = this.inferProviderFromSlug(model);
    if (inferred && !prefixes.includes(inferred)) prefixes.push(inferred);
    for (const p of this.indexes.providerPrefixesBySize) {
      if (!prefixes.includes(p)) prefixes.push(p);
    }

    for (const prefix of prefixes) {
      const candidate = `${prefix}/${model}`;
      let record = this.catalog.get(candidate);
      let modelId = candidate;
      if (!record) {
        const latest = this.findLatestVersioned(candidate);
        if (latest) {
          record = latest.record;
          modelId = latest.modelId;
        }
      }
      if (record) {
        const viaInference = inferred === prefix;
        return {
          modelId,
          record,
          confidence: 0.95,
          strategy: "provider-prefix-injection",
          reason: viaInference
            ? `Injected provider prefix "${prefix}/" inferred from model name pattern`
            : `Injected provider prefix "${prefix}/"`,
        };
      }
    }
    return null;
  }

  private crossProviderCorrection(model: string): MatchCandidate | null {
    if (!model.includes("/")) return null;
    const [wrongPrefix, ...rest] = model.split("/");
    const slug = rest.join("/");
    const inferred = this.inferProviderFromSlug(slug);
    if (!inferred || inferred === wrongPrefix) return null;
    const candidate = `${inferred}/${slug}`;
    let record = this.catalog.get(candidate);
    let modelId = candidate;
    if (!record) {
      const latest = this.findLatestVersioned(candidate);
      if (!latest) return null;
      record = latest.record;
      modelId = latest.modelId;
    }
    return {
      modelId,
      record,
      confidence: 0.9,
      strategy: "cross-provider-correction",
      reason: `Corrected provider prefix from "${wrongPrefix}" to "${inferred}" based on model name pattern`,
    };
  }

  private dateSuffixMatch(model: string): MatchCandidate | null {
    if (model.includes("/")) {
      const [prefix, slug] = model.split("/", 2);
      const stripped = stripModelSlugSuffix(slug);
      if (stripped && stripped !== slug) {
        const candidate = `${prefix}/${stripped}`;
        const record = this.catalog.get(candidate);
        if (record) {
          return {
            modelId: candidate,
            record,
            confidence: 0.85,
            strategy: "date-suffix-strip",
            reason: `Stripped date from slug "${slug}" → "${stripped}"`,
          };
        }
      }
    }

    const latest = this.findLatestVersioned(model);
    if (latest) {
      return {
        modelId: latest.modelId,
        record: latest.record,
        confidence: 0.85,
        strategy: "date-suffix-strip",
        reason: `Matched "${model}" to versioned catalog entry "${latest.modelId}" (latest)`,
      };
    }
    return null;
  }

  private findLatestVersioned(
    baseModelId: string,
  ): { modelId: string; record: AiModelRecord } | null {
    const candidates: Array<{ modelId: string; record: AiModelRecord; suffix: string }> = [];
    for (const [modelId, record] of this.catalog) {
      if (modelId === baseModelId) continue;
      if (modelId.startsWith(`${baseModelId}-`)) {
        candidates.push({
          modelId,
          record,
          suffix: modelId.slice(baseModelId.length + 1),
        });
      }
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => b.suffix.localeCompare(a.suffix));
    const best = candidates[0]!;
    return { modelId: best.modelId, record: best.record };
  }

  private partialMatch(
    model: string,
    provider: string | undefined,
  ): (MatchCandidate & { resolvedVia?: ResolutionStrategy[] }) | null {
    let best: { modelId: string; record: AiModelRecord; score: number } | null = null;
    const inputTokens = tokenise(model);
    if (inputTokens.length === 0) return null;

    for (const record of this.catalog.values()) {
      const recordTokens = new Set([
        ...tokenise(record.modelId),
        ...tokenise(record.name ?? ""),
      ]);
      const intersection = inputTokens.filter((t) => recordTokens.has(t));
      const overlapRatio = intersection.length / inputTokens.length;
      let score = overlapRatio * 0.6;

      if (record.modelId.includes(model)) score += 0.3;
      const slug = modelSlug(record.modelId);
      if (model.includes(slug)) score += 0.2;

      if (provider && provider === record.providerId) score += 0.15;

      const lenPenalty =
        (Math.abs(model.length - slug.length) / Math.max(model.length, slug.length, 1)) * 0.1;
      score -= lenPenalty;

      if (!best || score > best.score) {
        best = { modelId: record.modelId, record, score };
      }
    }

    if (!best || best.score < 0.65) return null;
    return {
      modelId: best.modelId,
      record: best.record,
      confidence: 0.65,
      strategy: "partial-name-match",
      reason: `Partial token match (score ${best.score.toFixed(2)})`,
    };
  }

  private computeRoutedViaOpenRouter(
    modelId: string,
    record: AiModelRecord | null,
    provider: string | undefined,
    originalProvider: string | undefined,
  ): boolean {
    const override = this.ctx.options.routeViaOpenRouter;
    if (override === true) return true;
    if (override === false) return false;

    const norm = normalizeProvider(originalProvider) ?? provider;
    if (norm === "openrouter") return true;
    if (norm && norm !== "openrouter") {
      if (shouldDefaultRouteViaOpenRouter(norm, this.ctx.options.routingEnv)) return true;
      return false;
    }

    const recordProvider = record?.providerId;
    if (recordProvider && shouldDefaultRouteViaOpenRouter(recordProvider, this.ctx.options.routingEnv)) {
      return true;
    }

    if (record?.availableOnOpenRouter && modelId.includes("/")) return true;
    return false;
  }

  private toOpenRouterSlug(
    modelId: string,
    provider: string | undefined,
    record: AiModelRecord | null,
  ): string {
    if (modelId.includes("/")) return modelId;
    const vendor =
      record?.providerId ??
      (provider && provider !== "openrouter" ? provider : undefined);
    if (!vendor || vendor === "openrouter") return modelId;
    return `${vendor}/${modelId}`;
  }

  private success(args: {
    modelId: string;
    record: AiModelRecord | null;
    confidence: number;
    strategy: ResolutionStrategy;
    reason: string;
    resolvedVia: ResolutionStrategy[];
    normalisedInput: string;
    provider?: string;
    originalProvider?: string;
  }): ModelResolutionSuccess {
    const routedViaOpenRouter = this.computeRoutedViaOpenRouter(
      args.modelId,
      args.record,
      args.provider,
      args.originalProvider,
    );
    const modelId = routedViaOpenRouter
      ? this.toOpenRouterSlug(args.modelId, args.provider, args.record)
      : args.modelId;

    return {
      found: true,
      modelId,
      record: args.record,
      routedViaOpenRouter,
      confidence: args.confidence,
      resolvedVia: args.resolvedVia,
      resolvedReason: args.reason,
      normalisedInput: args.normalisedInput,
    };
  }
}
